import { createContext, ProviderProps } from 'react';

import { iFrameSnippet, scriptSnippet } from './snippets';

declare global {
  interface Window {
    dataLayer: Record<string, any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  }
}

type ProviderPropsWithoutValue<T> = Omit<ProviderProps<T>, 'value'>;

const GTM_ID = process.env.VITE_APP_GTM_ID;
const GTM_AUTH = process.env.VITE_APP_GTM_AUTH;
const GTM_PREVIEW = process.env.VITE_APP_GTM_PREVIEW;

export interface GTM {
  initialize: () => void;
  push: (event: string, args?: Record<string, any>) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export type GTMContext = GTM | undefined;
export const gtmContext = createContext<GTMContext>(undefined);

export interface GTMProviderProps extends ProviderPropsWithoutValue<GTMContext> {
  noScript?: boolean; // Optional prop to control whether to include the noscript tag
}

export const GTMProvider = ({ children, noScript = false }: GTMProviderProps) => {
  const gtm: GTM = {
    initialize: () => {
      if (!GTM_ID || !GTM_AUTH || !GTM_PREVIEW) {
        return;
      }

      const iFrame = () => {
        const tag = document.createElement('noscript');
        tag.innerHTML = iFrameSnippet(GTM_ID, GTM_AUTH, GTM_PREVIEW);
        tag.setAttribute('id', 'gtm-noscript');
        return tag;
      };
      const script = () => {
        const tag = document.createElement('script');
        tag.innerHTML = scriptSnippet(GTM_ID, GTM_AUTH, GTM_PREVIEW);
        tag.setAttribute('id', 'gtm-script');
        return tag;
      };

      document.getElementById('gtm-noscript')?.remove();
      document.getElementById('gtm-script')?.remove();

      if (!document.getElementById('gtm-script')) {
        document.head.insertBefore(script(), document.head.firstChild);
      }

      if (!document.getElementById('gtm-noscript') && noScript) {
        document.body.insertBefore(iFrame(), document.body.firstChild);
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    push: (event: string, data?: Record<string, any>) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: event,
        ...data
      });
      if (data) {
        const cleanup = Object.keys(data).reduce(
          (acc, key) => {
            acc[key] = undefined;
            return acc;
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { event: 'gtm.clear' } as Record<string, any>
        );
        window.dataLayer.push(cleanup);
      }
    }
  };

  gtm.initialize();

  return <gtmContext.Provider value={gtm}>{children}</gtmContext.Provider>;
};
